<script setup lang="ts">
  import { computed, reactive, ref, watch } from 'vue'
  import {
    FormField,
    InputGroupInput,
    InputGroupNumber,
    InputGroupTextarea,
    InputGroupPhone,
    RadioCardGroup,
    RadioCardGroupItem,
    Checkbox,
    isValidPhoneNumber,
    Button,
    Label,
    TooltipHint,
    useAppLoading,
    useForm,
    useFormSubmit,
    z,
    ModalSection,
    ModalSectionHeader,
    ModalSectionHeaderTitle,
    ModalSectionHeaderDescription
  } from '@gat-ship/vue-components'
  import { FieldGroup } from '@gat-ship/vue-components'
  import {
    testIds,
    type StakeholderType,
    type FieldEntityTarget
  } from '@gat-ship/types'
  import {
    IconClose,
    IconCopy,
    IconEdit,
    IconLoading,
    IconPlus
  } from '@gat-ship/icons'
  import { text } from '@gat-ship/stakeholders'
  import {
    useCreateStakeholder,
    type CreateStakeholderFormApi
  } from '../../../../composables/useCreateStakeholder'
  import { useStakeholderMatchMessages } from '../../../../composables/useStakeholderMatchMessages'
  import { CountryPicker, CurrencyPicker } from '@gat-ship/ref-data'
  import { billingText } from '@gat-ship/stakeholders'
  import {
    CustomFieldForm,
    CustomFieldSlot,
    useCustomFields
  } from '@gat-ship/custom-fields'
  import { QuickContacts } from '@gat-ship/stakeholder-contacts'
  import DuplicateStakeholderProvider from '../../../providers/DuplicateStakeholderProvider.vue'

  const props = defineProps<{
    stakeholderType: StakeholderType
  }>()

  const stakeholderTypeToTarget: Record<StakeholderType, FieldEntityTarget> = {
    client: 'Stakeholder.Client',
    vendor: 'Stakeholder.Vendor',
    other: 'Stakeholder.OtherParty'
  }

  const entityTarget = computed(
    () => stakeholderTypeToTarget[props.stakeholderType]
  )

  const { customFields, isSystemFieldVisible, fieldProps } =
    useCustomFields(entityTarget)

  const { requestPayload, initializeRequestPayload } = useCreateStakeholder()
  const { buildMatchMessages } = useStakeholderMatchMessages()
  const customFieldValues = ref<Record<string, unknown>>({})

  interface SectionVisibility {
    postAddress: boolean
    visitAddress: boolean
    invoiceAddress: boolean
    registrationNumbers: boolean
    clientNumber: boolean
    creditLimit: boolean
    billingDetails: boolean
  }

  const show = reactive<SectionVisibility>({
    clientNumber: true,
    registrationNumbers: true,
    postAddress: true,
    visitAddress: false,
    invoiceAddress: false,
    creditLimit: true,
    billingDetails: true
  })

  const { isLoading } = useAppLoading()

  // Preserve the old FormPhoneField blur-time format validation (optional
  // field: empty allowed, non-empty must be a valid phone number).
  const phoneValidators = {
    onBlur: ({ value }: { value: string }) =>
      !value || value.length === 0
        ? undefined
        : isValidPhoneNumber(value)
          ? undefined
          : 'Invalid phone number'
  }

  const createStakeholderSchema = z
    .object({
      name: z.string().min(1, 'Name is required'),
      shortName: z.string().min(1, 'Short Name is required'),
      email: z.string().optional(),
      phoneNumber: z.string().optional(),
      countryCode: z.string().min(1, 'Country is required'),
      clientNumber: z.string().optional(),
      vendorNumber: z.string().optional(),
      taxIdentificationNumber: z.string().optional(),
      eoriNumber: z.string().optional(),
      postalAddress: z.string().optional(),
      visitAddressType: z.enum([
        'same-as-post-address',
        'custom-visit-address'
      ]),
      visitAddress: z.string().optional(),
      invoiceEmail: z.string().optional(),
      useDefaultBillingCurrency: z.enum(['default', 'custom']),
      billingCurrencyCode: z.string().optional(),
      useDefaultPaymentTerms: z.enum(['default', 'custom']),
      paymentTermsPeriodInDays: z.number().optional(),
      paymentTermsUseRunningMonth: z.boolean(),
      billingAddressType: z.enum(['same-as-postal', 'custom']),
      billingAddress: z.string().optional()
    })
    // Conditional requireds — each "Custom X" radio selection turns on a
    // nested required field. Schema is the single source of truth: the
    // matching FormField passes `required` only as a boolean (visual), and
    // the message + error state come from here on submit.
    .superRefine((data, ctx) => {
      const missingRequireds: Array<[boolean, string, string]> = [
        [
          data.visitAddressType === 'custom-visit-address' &&
            !data.visitAddress,
          'visitAddress',
          'Visit address is required'
        ],
        [
          data.useDefaultBillingCurrency === 'custom' &&
            !data.billingCurrencyCode,
          'billingCurrencyCode',
          'Currency is required'
        ],
        [
          data.useDefaultPaymentTerms === 'custom' &&
            (data.paymentTermsPeriodInDays === undefined ||
              data.paymentTermsPeriodInDays === null),
          'paymentTermsPeriodInDays',
          'Payment period is required'
        ],
        [
          data.billingAddressType === 'custom' && !data.billingAddress,
          'billingAddress',
          'Billing address is required'
        ]
      ]
      for (const [isMissing, path, message] of missingRequireds) {
        if (isMissing) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message })
        }
      }
    })
  type CreateStakeholderFormValues = z.infer<typeof createStakeholderSchema>

  const form = useForm({
    defaultValues: {
      name: '',
      shortName: '',
      email: '',
      phoneNumber: '',
      countryCode: '',
      clientNumber: '',
      vendorNumber: '',
      taxIdentificationNumber: '',
      eoriNumber: '',
      postalAddress: '',
      visitAddressType: 'same-as-post-address',
      visitAddress: '',
      invoiceEmail: '',
      useDefaultBillingCurrency: 'default',
      billingCurrencyCode: '',
      useDefaultPaymentTerms: 'default',
      paymentTermsPeriodInDays: undefined,
      paymentTermsUseRunningMonth: false,
      billingAddressType: 'same-as-postal',
      billingAddress: ''
    } as CreateStakeholderFormValues,
    validators: {
      onSubmit: createStakeholderSchema
    }
  })

  // Sync form values back to requestPayload for the provider.
  // `form.useStore((s) => s.values)` is tanstack's reactive selector; using
  // `() => form.state.values` as a watch source doesn't trigger because the
  // underlying store isn't a Vue-reactive reference.
  const formValues = form.useStore((s) => s.values)

  // Reactive countryCode for the phone field's auto-prefill behavior —
  // InputGroupPhone forwards this prop to PhoneInput, which watches it and
  // pre-fills the dial code visually when the field is empty. Must be a
  // reactive selector (not
  // `form.state.values.countryCode`, which is a plain snapshot).
  const selectedCountryCode = form.useStore((s) => s.values.countryCode)
  type RequestPayloadValue = NonNullable<typeof requestPayload.value>

  /** Optional text inputs sync as `undefined` when left empty. */
  const orUndefined = (value: string | undefined) => value || undefined

  function syncContactFields(
    payload: RequestPayloadValue,
    values: CreateStakeholderFormValues
  ) {
    payload.name = values.name
    payload.shortName = values.shortName
    payload.email = orUndefined(values.email)
    payload.phoneNumber = orUndefined(values.phoneNumber)
    payload.countryCode = orUndefined(values.countryCode)
    payload.postalAddress = orUndefined(values.postalAddress)
  }

  function syncAddressFields(
    payload: RequestPayloadValue,
    values: CreateStakeholderFormValues
  ) {
    payload.visitAddressSameAsPostal =
      values.visitAddressType === 'same-as-post-address'
    payload.visitAddress =
      values.visitAddressType === 'custom-visit-address'
        ? orUndefined(values.visitAddress)
        : undefined
    if ('billingAddressSameAsPostal' in payload) {
      payload.billingAddressSameAsPostal =
        values.billingAddressType === 'same-as-postal'
    }
    if ('billingAddress' in payload) {
      payload.billingAddress =
        values.billingAddressType === 'custom'
          ? orUndefined(values.billingAddress)
          : undefined
    }
  }

  function syncIdentificationFields(
    payload: RequestPayloadValue,
    values: CreateStakeholderFormValues
  ) {
    if (props.stakeholderType === 'client' && 'clientNumber' in payload) {
      payload.clientNumber = orUndefined(values.clientNumber)
    }
    if (props.stakeholderType === 'vendor' && 'vendorNumber' in payload) {
      payload.vendorNumber = orUndefined(values.vendorNumber)
    }
    if ('taxIdentificationNumber' in payload) {
      payload.taxIdentificationNumber = orUndefined(
        values.taxIdentificationNumber
      )
    }
    if ('eoriNumber' in payload) {
      payload.eoriNumber = orUndefined(values.eoriNumber)
    }
    if ('invoiceEmail' in payload) {
      payload.invoiceEmail = orUndefined(values.invoiceEmail)
    }
  }

  function syncBillingFields(
    payload: RequestPayloadValue,
    values: CreateStakeholderFormValues
  ) {
    if ('useDefaultBillingCurrency' in payload) {
      payload.useDefaultBillingCurrency =
        values.useDefaultBillingCurrency === 'default'
    }
    if ('billingCurrencyCode' in payload) {
      payload.billingCurrencyCode =
        values.useDefaultBillingCurrency === 'custom'
          ? orUndefined(values.billingCurrencyCode)
          : undefined
    }
    if ('useDefaultPaymentTerms' in payload) {
      payload.useDefaultPaymentTerms =
        values.useDefaultPaymentTerms === 'default'
    }
    if ('paymentTermsPeriodInDays' in payload) {
      payload.paymentTermsPeriodInDays =
        values.useDefaultPaymentTerms === 'custom'
          ? values.paymentTermsPeriodInDays
          : undefined
    }
    if ('paymentTermsUseRunningMonth' in payload) {
      payload.paymentTermsUseRunningMonth =
        values.useDefaultPaymentTerms === 'custom'
          ? values.paymentTermsUseRunningMonth
          : undefined
    }
  }

  watch(
    formValues,
    (values) => {
      const payload = requestPayload.value
      if (!payload) return
      syncContactFields(payload, values)
      syncAddressFields(payload, values)
      syncIdentificationFields(payload, values)
      syncBillingFields(payload, values)
    },
    { deep: true }
  )

  watch(
    customFieldValues,
    (values) => {
      if (!requestPayload.value) return
      requestPayload.value.customFields = values
    },
    { deep: true }
  )

  function applyAiCustomFieldValues(
    rawCustomFields: Record<string, { value?: unknown }> | undefined
  ) {
    if (!rawCustomFields) return
    const cfUpdates: Record<string, unknown> = { ...customFieldValues.value }
    for (const [key, entry] of Object.entries(rawCustomFields)) {
      if (entry?.value !== undefined && entry?.value !== null) {
        cfUpdates[key] = entry.value
      }
    }
    customFieldValues.value = cfUpdates
  }

  function applyAiValues(values: Record<string, unknown>) {
    const stringFields: Array<keyof CreateStakeholderFormValues> = [
      'name',
      'shortName',
      'email',
      'phoneNumber',
      'countryCode',
      'clientNumber',
      'vendorNumber',
      'taxIdentificationNumber',
      'eoriNumber',
      'postalAddress',
      'invoiceEmail'
    ]
    const updates: Record<string, string> = {}
    for (const key of stringFields) {
      const entry = values[key] as { value?: string } | undefined
      if (entry?.value) updates[key] = entry.value
    }
    form.reset({ ...form.state.values, ...updates })

    applyAiCustomFieldValues(
      values.customFields as Record<string, { value?: unknown }> | undefined
    )
  }

  const dynCustomFieldsRef = ref<InstanceType<typeof CustomFieldForm> | null>(
    null
  )

  // The provider orchestrates validate → map → API → emit → optimistic
  // update and invokes this form's `validate()` from its
  // `commands.createStakeholder()`. We expose a small, purpose-built
  // `CreateStakeholderFormApi` (validate + applyAiValues) rather than the
  // old v1 `GatForm` bridge — the provider never needed the rest of that
  // surface. `validate()` reproduces the bridge's behaviour: run the
  // tanstack submit (flush change validators + handleSubmit +
  // focus-first-error), then gate on field errors and the dynamic
  // custom-fields validator.
  const { submit, errorMessage } = useFormSubmit(form)

  async function validate(): Promise<boolean> {
    await submit()
    if (errorMessage.value) {
      return false
    }
    if (hasFieldErrors()) {
      return false
    }
    return (await dynCustomFieldsRef.value?.validate()) ?? true
  }

  function hasFieldErrors(): boolean {
    const meta = (
      form as unknown as {
        state: {
          fieldMeta?: Record<string, { errors?: unknown[] } | undefined>
        }
      }
    ).state.fieldMeta
    if (!meta) {
      return false
    }
    for (const fieldMeta of Object.values(meta)) {
      if (fieldMeta?.errors && fieldMeta.errors.length > 0) {
        return true
      }
    }
    return false
  }

  const formApi: CreateStakeholderFormApi = { validate, applyAiValues }

  function onFormMounted() {
    initializeRequestPayload({
      stakeholderType: props.stakeholderType,
      formApi
    })
  }

  const nameMessagesAsync = buildMatchMessages(
    {
      type: props.stakeholderType,
      name_matching: 'similar',
      name: '',
      limit: 6
    },
    {
      exactMatchMessage: 'This name is already in use',
      similarMessage: (count) =>
        `${count} ${props.stakeholderType}(s) with similar names found`
    }
  )
  const emailMessagesAsync = buildMatchMessages(
    { type: props.stakeholderType, name_matching: 'exact', email: '' },
    {
      exactMatchMessage: `A ${props.stakeholderType} with this email already exists`,
      variant: 'warning'
    }
  )
  const phoneMessagesAsync = buildMatchMessages(
    { type: props.stakeholderType, name_matching: 'exact', phone: '' },
    {
      exactMatchMessage: `A ${props.stakeholderType} with this phone number already exists`,
      variant: 'warning'
    }
  )
  const tinMessagesAsync = buildMatchMessages(
    { type: props.stakeholderType, name_matching: 'exact', tin: '' },
    { exactMatchMessage: 'This tax identification number is already in use' }
  )
  const eoriMessagesAsync = buildMatchMessages(
    { type: props.stakeholderType, name_matching: 'exact', eori_number: '' },
    { exactMatchMessage: 'This EORI number is already in use' }
  )
  const clientNumberMessagesAsync = buildMatchMessages(
    { type: 'client', name_matching: 'exact', client_number: '' },
    { exactMatchMessage: 'This client number is already in use' }
  )
  const vendorNumberMessagesAsync = buildMatchMessages(
    { type: 'vendor', name_matching: 'exact', vendor_number: '' },
    { exactMatchMessage: 'This vendor number is already in use' }
  )
</script>

<template>
  <DuplicateStakeholderProvider>
    <div class="flex flex-col gap-5" @vue:mounted="onFormMounted">
      <ModalSection
        v-if="!requestPayload"
        class="text-muted-foreground flex items-center gap-2 text-sm"
      >
        <IconLoading class="size-4" />Initializing...
      </ModalSection>
      <template v-else>
        <!-- General Information -->
        <ModalSection>
          <ModalSectionHeader>
            <ModalSectionHeaderTitle>
              {{ text.groups.generalInformation(stakeholderType).title }}
            </ModalSectionHeaderTitle>
            <ModalSectionHeaderDescription>
              {{ text.groups.generalInformation(stakeholderType).description }}
            </ModalSectionHeaderDescription>
          </ModalSectionHeader>
          <FieldGroup>
            <FormField
              :form="form"
              name="name"
              label="Name"
              required
              v-bind="fieldProps('name')"
              :messages-async="nameMessagesAsync"
            >
              <template #default="{ field }">
                <InputGroupInput
                  :id="field.name"
                  :model-value="field.state.value ?? ''"
                  autofocus
                  :data-testid="testIds.stakeholder.forms.generalInfo.nameInput"
                  @update:model-value="field.handleChange"
                  @blur="field.handleBlur"
                />
              </template>
            </FormField>
            <FormField
              :form="form"
              name="shortName"
              label="Short Name"
              required
              hint="Short name is commonly used for search and display purposes."
              v-bind="fieldProps('shortName')"
            >
              <template #default="{ field }">
                <InputGroupInput
                  :id="field.name"
                  :model-value="field.state.value ?? ''"
                  :data-testid="
                    testIds.stakeholder.forms.generalInfo.shortNameInput
                  "
                  @update:model-value="field.handleChange"
                  @blur="field.handleBlur"
                />
              </template>
            </FormField>
            <FormField
              :form="form"
              name="countryCode"
              label="Country"
              required
              v-bind="fieldProps('countryCode')"
            >
              <template #default="{ field, invalid }">
                <CountryPicker
                  :id="field.name"
                  :model-value="field.state.value"
                  :aria-invalid="invalid"
                  :data-testid="
                    testIds.stakeholder.forms.generalInfo.countryInput
                  "
                  @update:model-value="field.handleChange"
                  @blur="field.handleBlur"
                />
              </template>
            </FormField>
          </FieldGroup>
          <FieldGroup
            v-if="
              !isSystemFieldVisible('email') === false ||
              !isSystemFieldVisible('phoneNumber') === false
            "
          >
            <FormField
              v-if="isSystemFieldVisible('email')"
              :form="form"
              name="email"
              label="Email"
              v-bind="fieldProps('email')"
              :messages-async="emailMessagesAsync"
            >
              <template #default="{ field }">
                <InputGroupInput
                  :id="field.name"
                  :model-value="field.state.value ?? ''"
                  :data-testid="
                    testIds.stakeholder.forms.generalInfo.emailInput
                  "
                  @update:model-value="field.handleChange"
                  @blur="field.handleBlur"
                />
              </template>
            </FormField>
            <FormField
              v-if="isSystemFieldVisible('phoneNumber')"
              :form="form"
              name="phoneNumber"
              label="Phone"
              v-bind="fieldProps('phoneNumber')"
              :validators="phoneValidators"
              :messages-async="phoneMessagesAsync"
            >
              <template #default="{ field }">
                <InputGroupPhone
                  :id="field.name"
                  :model-value="field.state.value ?? undefined"
                  :country-code="selectedCountryCode"
                  :data-testid="
                    testIds.stakeholder.forms.generalInfo.phoneInput
                  "
                  @update:model-value="(v) => field.handleChange(v ?? '')"
                  @blur="field.handleBlur"
                />
              </template>
            </FormField>
          </FieldGroup>
          <CustomFieldForm
            v-if="customFields.length > 0"
            ref="dynCustomFieldsRef"
            v-model="customFieldValues"
            :target="entityTarget"
          />
        </ModalSection>

        <!-- Internal Identification Numbers -->
        <CustomFieldSlot name="internalIdentificationNumbers">
          <ModalSection
            v-if="
              (stakeholderType === 'client' &&
                isSystemFieldVisible('clientNumber')) ||
              (stakeholderType === 'vendor' &&
                isSystemFieldVisible('vendorNumber'))
            "
            class="relative"
          >
            <ModalSectionHeader>
              <ModalSectionHeaderTitle>
                {{
                  text.groups.internalIdentificationNumbers(stakeholderType)
                    .title
                }}
              </ModalSectionHeaderTitle>
              <ModalSectionHeaderDescription>
                {{
                  text.groups.internalIdentificationNumbers(stakeholderType)
                    .description
                }}
              </ModalSectionHeaderDescription>
            </ModalSectionHeader>
            <FieldGroup>
              <FormField
                v-if="
                  stakeholderType === 'client' &&
                  'clientNumber' in requestPayload
                "
                :form="form"
                name="clientNumber"
                label="Client Number"
                v-bind="fieldProps('clientNumber')"
                :messages-async="clientNumberMessagesAsync"
              >
                <template #default="{ field }">
                  <InputGroupInput
                    :id="field.name"
                    :model-value="field.state.value ?? ''"
                    :data-testid="
                      testIds.stakeholders.forms.internalIdentificationNumbers
                        .clientNumberInput
                    "
                    @update:model-value="field.handleChange"
                    @blur="field.handleBlur"
                  />
                </template>
              </FormField>
              <FormField
                v-if="
                  stakeholderType === 'vendor' &&
                  'vendorNumber' in requestPayload
                "
                :form="form"
                name="vendorNumber"
                label="Vendor Number"
                hint="The vendor number is primarily used for integrations with external systems."
                v-bind="fieldProps('vendorNumber')"
                :messages-async="vendorNumberMessagesAsync"
              >
                <template #default="{ field }">
                  <InputGroupInput
                    :id="field.name"
                    :model-value="field.state.value ?? ''"
                    :data-testid="
                      testIds.stakeholders.forms.internalIdentificationNumbers
                        .vendorNumberInput
                    "
                    @update:model-value="field.handleChange"
                    @blur="field.handleBlur"
                  />
                </template>
              </FormField>
            </FieldGroup>
          </ModalSection>
        </CustomFieldSlot>

        <!-- Official Business Numbers -->
        <ModalSection
          v-if="
            (stakeholderType === 'client' || stakeholderType === 'vendor') &&
            (isSystemFieldVisible('taxIdentificationNumber') ||
              isSystemFieldVisible('eoriNumber'))
          "
          class="relative"
        >
          <ModalSectionHeader>
            <ModalSectionHeaderTitle>
              {{ text.groups.officialBusinessNumbers(stakeholderType).title }}
            </ModalSectionHeaderTitle>
            <ModalSectionHeaderDescription>
              {{
                text.groups.officialBusinessNumbers(stakeholderType).description
              }}
            </ModalSectionHeaderDescription>
          </ModalSectionHeader>
          <FieldGroup
            v-if="
              show.registrationNumbers &&
              'taxIdentificationNumber' in requestPayload &&
              'eoriNumber' in requestPayload
            "
          >
            <FormField
              v-if="
                stakeholderType === 'client' || stakeholderType === 'vendor'
              "
              :form="form"
              name="taxIdentificationNumber"
              label="Tax Identification Number"
              hint="Enter the official tax or registration number of this company. This ensures correct invoicing and tax handling. Examples: VAT (EU), EIN (US), Org.nr (Norway), CNPJ (Brazil)."
              v-bind="fieldProps('taxIdentificationNumber')"
              :messages-async="tinMessagesAsync"
            >
              <template #default="{ field }">
                <InputGroupInput
                  :id="field.name"
                  :model-value="field.state.value ?? ''"
                  :data-testid="
                    testIds.stakeholders.forms.officialBusinessNumbers
                      .taxIdentificationNumberInput
                  "
                  @update:model-value="field.handleChange"
                  @blur="field.handleBlur"
                />
              </template>
            </FormField>
            <FormField
              v-if="
                stakeholderType === 'client' || stakeholderType === 'vendor'
              "
              :form="form"
              name="eoriNumber"
              label="EORI Number"
              hint="Your EORI number identifies you for customs procedures within the EU and UK. Use the full number including the country prefix (e.g., GB123456789000)."
              v-bind="fieldProps('eoriNumber')"
              :messages-async="eoriMessagesAsync"
            >
              <template #default="{ field }">
                <InputGroupInput
                  :id="field.name"
                  :model-value="field.state.value ?? ''"
                  :data-testid="
                    testIds.stakeholders.forms.officialBusinessNumbers
                      .eoriNumberInput
                  "
                  @update:model-value="field.handleChange"
                  @blur="field.handleBlur"
                />
              </template>
            </FormField>
          </FieldGroup>
        </ModalSection>

        <!-- Addresses -->
        <ModalSection
          v-if="
            isSystemFieldVisible('postalAddress') ||
            isSystemFieldVisible('visitAddress')
          "
          class="relative"
        >
          <ModalSectionHeader>
            <ModalSectionHeaderTitle>
              {{ text.groups.address.title }}
            </ModalSectionHeaderTitle>
            <ModalSectionHeaderDescription>
              {{ text.groups.address.description }}
            </ModalSectionHeaderDescription>
          </ModalSectionHeader>
          <FieldGroup v-if="isSystemFieldVisible('postalAddress')">
            <FormField
              :form="form"
              name="postalAddress"
              :label="text.groups.postalAddress('client').title"
              :hint="text.groups.postalAddress('client').description"
              v-bind="fieldProps('postalAddress')"
            >
              <template #default="{ field }">
                <InputGroupTextarea
                  :id="field.name"
                  :model-value="field.state.value ?? ''"
                  :data-testid="
                    testIds.stakeholders.forms.postalAddress.addressInput
                  "
                  @update:model-value="field.handleChange"
                  @blur="field.handleBlur"
                />
              </template>
            </FormField>
          </FieldGroup>
          <template v-if="isSystemFieldVisible('visitAddress')">
            <Button
              v-if="!show.visitAddress"
              :disabled="isLoading(requestPayload.id)"
              class="text-muted-foreground w-full"
              variant="outline"
              size="sm"
              :data-testid="
                testIds.stakeholders.forms.visitAddress.addVisitAddressButton
              "
              @click="show.visitAddress = true"
            >
              <IconPlus class="size-4" />Add Visit Address
            </Button>
            <div v-else class="flex flex-col pb-1.5 pt-2.5">
              <Label
                class="grid min-h-6 w-full select-none grid-cols-[1fr_auto] items-end gap-0.5 text-xs font-medium text-neutral-700 dark:text-neutral-200"
              >
                <div class="flex items-end gap-0.5 pb-1.5">
                  <span>{{
                    text.groups.visitAddress(stakeholderType).title
                  }}</span>
                  <TooltipHint
                    v-if="text.groups.visitAddress(stakeholderType).description"
                    icon-class="size-4"
                    :text="
                      text.groups.visitAddress(stakeholderType).description
                    "
                  />
                  <span class="text-muted-foreground text-xs font-normal"
                    >optional</span
                  >
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  class="text-muted-foreground -mb-px h-6 gap-1 rounded-b-none border-neutral-300 bg-neutral-50 text-xs"
                  :data-testid="
                    testIds.stakeholders.forms.visitAddress
                      .removeVisitAddressButton
                  "
                  @click="show.visitAddress = false"
                >
                  <span>remove</span>
                  <IconClose class="size-3" />
                </Button>
              </Label>
              <FormField
                :form="form"
                name="visitAddressType"
                :input-group="false"
              >
                <template #default="{ field }">
                  <RadioCardGroup
                    :model-value="field.state.value"
                    @update:model-value="field.handleChange"
                  >
                    <RadioCardGroupItem
                      value="same-as-post-address"
                      label="Same as Post Address"
                      description="The visit address is the same as the post address"
                      :icon="IconCopy"
                      :data-testid="
                        testIds.stakeholders.forms.visitAddress
                          .sameAsPostalRadio
                      "
                    />
                    <RadioCardGroupItem
                      value="custom-visit-address"
                      label="Custom Visit Address"
                      description="Manually set the visit address"
                      :icon="IconEdit"
                      :data-testid="
                        testIds.stakeholders.forms.visitAddress
                          .customVisitAddressRadio
                      "
                    >
                      <template #footer="{ selected }">
                        <FieldGroup v-if="selected">
                          <FormField
                            :form="form"
                            name="visitAddress"
                            label="Visit Address"
                            required
                            v-bind="fieldProps('visitAddress')"
                          >
                            <template #default="{ field: visitField }">
                              <InputGroupTextarea
                                :id="visitField.name"
                                :model-value="visitField.state.value ?? ''"
                                autofocus
                                :data-testid="
                                  testIds.stakeholders.forms.visitAddress
                                    .addressInput
                                "
                                @update:model-value="visitField.handleChange"
                                @blur="visitField.handleBlur"
                              />
                            </template>
                          </FormField>
                        </FieldGroup>
                      </template>
                    </RadioCardGroupItem>
                  </RadioCardGroup>
                </template>
              </FormField>
            </div>
          </template>
        </ModalSection>

        <!-- Billing Information (client only) -->
        <ModalSection
          v-if="
            stakeholderType === 'client' &&
            'useDefaultPaymentTerms' in requestPayload &&
            'paymentTermsUseRunningMonth' in requestPayload &&
            'invoiceEmail' in requestPayload &&
            'billingCurrencyCode' in requestPayload &&
            'useDefaultBillingCurrency' in requestPayload &&
            'paymentTermsPeriodInDays' in requestPayload &&
            'billingAddressSameAsPostal' in requestPayload &&
            'billingAddress' in requestPayload
          "
          class="relative"
        >
          <ModalSectionHeader>
            <ModalSectionHeaderTitle>
              {{ text.groups.billingInformation(stakeholderType).title }}
            </ModalSectionHeaderTitle>
            <ModalSectionHeaderDescription>
              {{ text.groups.billingInformation(stakeholderType).description }}
            </ModalSectionHeaderDescription>
          </ModalSectionHeader>
          <!-- Invoice Email -->
          <FieldGroup>
            <FormField
              :form="form"
              name="invoiceEmail"
              label="Invoice Email"
              hint="Email address of the invoice recipient"
              v-bind="fieldProps('invoiceEmail')"
            >
              <template #default="{ field }">
                <InputGroupInput
                  :id="field.name"
                  :model-value="field.state.value ?? ''"
                  :data-testid="
                    testIds.stakeholders.forms.billingDetails.invoiceEmailInput
                  "
                  @update:model-value="field.handleChange"
                  @blur="field.handleBlur"
                />
              </template>
            </FormField>
          </FieldGroup>

          <!-- Invoice Currency -->
          <FormField
            :form="form"
            name="useDefaultBillingCurrency"
            label="Invoice Currency"
            required
            :input-group="false"
          >
            <template #default="{ field: currencyTypeField }">
              <RadioCardGroup
                :model-value="currencyTypeField.state.value"
                @update:model-value="currencyTypeField.handleChange"
              >
                <RadioCardGroupItem
                  value="default"
                  label="Default Invoice Currency"
                  description="From Agency Settings"
                  :icon="IconCopy"
                  :data-testid="
                    testIds.stakeholders.forms.billingDetails
                      .defaultInvoiceCurrencyRadio
                  "
                />
                <RadioCardGroupItem
                  value="custom"
                  label="Custom Invoice Currency"
                  description="Manually set the invoice currency"
                  :icon="IconEdit"
                  :data-testid="
                    testIds.stakeholders.forms.billingDetails
                      .customInvoiceCurrencyRadio
                  "
                >
                  <template #footer="{ selected }">
                    <FieldGroup v-if="selected">
                      <FormField
                        :form="form"
                        name="billingCurrencyCode"
                        label="Currency"
                        required
                      >
                        <template #default="{ field, invalid }">
                          <CurrencyPicker
                            :id="field.name"
                            :model-value="field.state.value"
                            :aria-invalid="invalid"
                            :data-testid="
                              testIds.stakeholders.forms.billingDetails
                                .invoiceCurrencyInput
                            "
                            @update:model-value="field.handleChange"
                            @blur="field.handleBlur"
                          />
                        </template>
                      </FormField>
                    </FieldGroup>
                  </template>
                </RadioCardGroupItem>
              </RadioCardGroup>
            </template>
          </FormField>

          <!-- Payment Terms -->
          <FormField
            :form="form"
            name="useDefaultPaymentTerms"
            label="Payment Terms"
            :input-group="false"
            required
          >
            <template #default="{ field }">
              <RadioCardGroup
                :model-value="field.state.value"
                @update:model-value="field.handleChange"
              >
                <RadioCardGroupItem
                  value="default"
                  label="Default Payment Terms"
                  description="From Agency Settings"
                  :icon="IconCopy"
                  :data-testid="
                    testIds.stakeholders.forms.billingDetails
                      .defaultPaymentTermsRadio
                  "
                />
                <RadioCardGroupItem
                  value="custom"
                  label="Custom Payment Terms"
                  description="Manually set the payment terms"
                  :icon="IconEdit"
                  :data-testid="
                    testIds.stakeholders.forms.billingDetails
                      .customPaymentTermsRadio
                  "
                >
                  <template #footer="{ selected }">
                    <FieldGroup v-if="selected">
                      <FormField
                        :form="form"
                        name="paymentTermsPeriodInDays"
                        :label="billingText.inputs.paymentPeriod.label"
                        :hint="billingText.inputs.paymentPeriod.tooltipHint"
                        required
                      >
                        <template #default="{ field: periodField }">
                          <InputGroupNumber
                            :id="periodField.name"
                            :model-value="periodField.state.value"
                            :placeholder="
                              billingText.inputs.paymentPeriod.placeholder
                            "
                            :data-testid="
                              testIds.stakeholders.forms.billingDetails
                                .creditDaysInput
                            "
                            @update:model-value="periodField.handleChange"
                            @blur="periodField.handleBlur"
                          />
                        </template>
                      </FormField>
                      <component
                        :is="form.Field"
                        name="paymentTermsUseRunningMonth"
                      >
                        <template #default="{ field: runningMonthField }">
                          <label
                            class="group/field flex cursor-pointer items-start gap-2 px-3 py-2"
                          >
                            <Checkbox
                              :model-value="!!runningMonthField.state.value"
                              :data-testid="
                                testIds.stakeholders.forms.billingDetails
                                  .useCreditRunningMonthCheckbox
                              "
                              @update:model-value="
                                (v) => {
                                  runningMonthField.handleChange(v === true)
                                  runningMonthField.handleBlur()
                                }
                              "
                            />
                            <span class="flex flex-col gap-1">
                              <span
                                class="text-xs font-medium leading-4 text-neutral-700 dark:text-neutral-200"
                              >
                                {{ billingText.inputs.useEndOfMonth.label }}
                              </span>
                              <span
                                class="text-muted-foreground whitespace-pre-line text-xs leading-snug"
                              >
                                {{
                                  billingText.inputs.useEndOfMonth.description
                                }}
                              </span>
                            </span>
                          </label>
                        </template>
                      </component>
                    </FieldGroup>
                  </template>
                </RadioCardGroupItem>
              </RadioCardGroup>
            </template>
          </FormField>

          <!-- Billing Address -->
          <FormField
            :form="form"
            name="billingAddressType"
            label="Billing Address"
            :input-group="false"
            required
          >
            <template #default="{ field }">
              <RadioCardGroup
                :model-value="field.state.value"
                @update:model-value="field.handleChange"
              >
                <RadioCardGroupItem
                  value="same-as-postal"
                  label="Same as Postal Address"
                  description="The billing address is the same as the postal address"
                  :icon="IconCopy"
                  :data-testid="
                    testIds.stakeholders.forms.billingAddress.sameAsPostalRadio
                  "
                />
                <RadioCardGroupItem
                  value="custom"
                  label="Custom Billing Address"
                  description="Manually set the billing address"
                  :icon="IconEdit"
                  :data-testid="
                    testIds.stakeholders.forms.billingAddress
                      .customBillingAddressRadio
                  "
                >
                  <template #footer="{ selected }">
                    <FieldGroup v-if="selected">
                      <FormField
                        :form="form"
                        name="billingAddress"
                        label="Billing Address"
                        required
                      >
                        <template #default="{ field: billingField }">
                          <InputGroupTextarea
                            :id="billingField.name"
                            :model-value="billingField.state.value ?? ''"
                            :data-testid="
                              testIds.stakeholders.forms.billingAddress
                                .addressInput
                            "
                            @update:model-value="billingField.handleChange"
                            @blur="billingField.handleBlur"
                          />
                        </template>
                      </FormField>
                    </FieldGroup>
                  </template>
                </RadioCardGroupItem>
              </RadioCardGroup>
            </template>
          </FormField>
        </ModalSection>

        <!-- Contact Persons -->
        <ModalSection class="relative">
          <ModalSectionHeader>
            <ModalSectionHeaderTitle>
              {{ text.groups.contactPersons.title }}
            </ModalSectionHeaderTitle>
            <ModalSectionHeaderDescription>
              {{ text.groups.contactPersons.description }}
            </ModalSectionHeaderDescription>
          </ModalSectionHeader>
          <QuickContacts v-model:contacts="requestPayload.mainContacts" />
        </ModalSection>
      </template>
    </div>
  </DuplicateStakeholderProvider>
</template>
